import {Injectable} from '@angular/core';
import {
  ADMIN_DASHBOARD_REPORT_SECTIONS,
  AdminDashboardReportBlock,
  AdminDashboardReportProvider,
  AdminDashboardReportSection,
  AdminDashboardReportSectionKey
} from '@features/admin-panel/models/admin-dashboard-report.models';

/** One registered card: its report section, its host element (for page order) and the card itself. */
interface AdminDashboardReportEntry {
  section: AdminDashboardReportSectionKey;
  element: HTMLElement;
  provider: AdminDashboardReportProvider;
}

/**
 * The dashboard cards that can describe themselves for the PDF report (ITP-E-146). A card whose feature is not
 * granted is never rendered, so it never registers.
 */
@Injectable({providedIn: 'root'})
export class AdminDashboardReportRegistry {
  private entries: AdminDashboardReportEntry[] = [];

  /** A card registers itself in ngOnInit with its host element (for DOM order) and unregisters in ngOnDestroy. */
  register(section: AdminDashboardReportSectionKey, element: HTMLElement, provider: AdminDashboardReportProvider): void {
    this.unregister(provider);
    this.entries.push({section, element, provider});
  }

  unregister(provider: AdminDashboardReportProvider): void {
    this.entries = this.entries.filter(entry => entry.provider !== provider);
  }

  /** Sections in ADMIN_DASHBOARD_REPORT_SECTIONS order; inside a section, providers in DOM order
   *  (Node.compareDocumentPosition); blocks concatenated; sections with no blocks dropped. A card whose widget is
   *  'empty' is left out, as the dashboard hides it (ITP-E-173-01). A provider that throws is skipped
   *  (console.warn) so one card never breaks the report. */
  collect(): AdminDashboardReportSection[] {
    return ADMIN_DASHBOARD_REPORT_SECTIONS
      .map(section => ({key: section.key, title: section.title, subtitle: section.subtitle, blocks: this.blocksOf(section.key)}))
      .filter(section => section.blocks.length > 0);
  }

  private blocksOf(section: AdminDashboardReportSectionKey): AdminDashboardReportBlock[] {
    return this.entries
      .filter(entry => entry.section === section)
      .sort((a, b) => pageOrder(a.element, b.element))
      .reduce((blocks, entry) => blocks.concat(this.blocksFrom(entry.provider)), [] as AdminDashboardReportBlock[]);
  }

  private blocksFrom(provider: AdminDashboardReportProvider): AdminDashboardReportBlock[] {
    if (provider.widgetState === 'empty') {
      return [];
    }
    try {
      return provider.reportBlocks() ?? [];
    } catch (error) {
      console.warn('Admin dashboard report: a card could not describe itself and is left out.', error);
      return [];
    }
  }
}

/** Negative when `a` comes before `b` on the page. */
function pageOrder(a: HTMLElement, b: HTMLElement): number {
  if (a === b) {
    return 0;
  }
  return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}
